"use client";

import { Phone, PhoneOff, X, ExternalLink, PauseCircle, CornerDownRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";

interface IncomingCallWidgetProps {
  callerName: string;
  callerNumber: string;
  callId: string;
  leadId?: string;
  isPooledLead?: boolean;
  isLiveTransfer?: boolean;
  campaignName?: string;
  /** A TV line's tag (e.g. "TV Ad"): tells the agent it's an ad caller before they pick up. */
  sourceLabel?: string;
  isCallWaiting?: boolean;
  onAnswer: (options?: { holdActive?: boolean; mergeActive?: boolean }) => void;
  onDecline: () => void;
}

export const IncomingCallWidget = function ({
  callerName,
  callerNumber,
  callId,
  leadId,
  isPooledLead,
  isLiveTransfer,
  campaignName,
  sourceLabel,
  isCallWaiting = false,
  onAnswer,
  onDecline,
}: IncomingCallWidgetProps) {
  const router = useRouter();
  const [ringDuration, setRingDuration] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setRingDuration((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key === "Enter") {
        e.preventDefault();
        if (isCallWaiting) onAnswer({ holdActive: true });
        else onAnswer();
      } else if (e.key === ".") {
        e.preventDefault();
        onDecline();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isCallWaiting, onAnswer, onDecline]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const formatPhoneNumber = (phone: string) => {
    const cleaned = phone.replace(/\D/g, "");
    const digits = cleaned.length === 11 && cleaned.startsWith("1") ? cleaned.slice(1) : cleaned.slice(-10);
    return digits.length === 10 ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}` : phone;
  };

  const handleViewLeadDetails = () => {
    // Inside the native mobile shell every screen lives under /m — routing to
    // the desktop lead page from there strands the user outside the app chrome.
    const isMobileShell = typeof window !== "undefined" && window.location.pathname.startsWith("/m");
    if (leadId) router.push(`${isMobileShell ? "/m" : ""}/leads/${leadId}`);
  };

  const initial = (callerName || "").trim().charAt(0).toUpperCase() || "?";

  return (
    <div
      role="dialog"
      aria-label={isCallWaiting ? "Call waiting" : "Incoming call"}
      className="icw-root"
    >
      <div className="icw-accent-bar" aria-hidden="true" />

      <div className="icw-top">
        <div className="icw-eyebrow">
          <span className="icw-pulse-dot" aria-hidden="true" />
          <span className="icw-eyebrow-text">
            {isCallWaiting ? "Call waiting" : "Incoming call"}
          </span>
        </div>
        <div className="icw-top-right">
          <span className="icw-duration">{formatDuration(ringDuration)}</span>
          <button className="icw-close" onClick={onDecline} aria-label="Dismiss">
            <X strokeWidth={1.75} />
          </button>
        </div>
      </div>

      <div className="icw-identity">
        <div className="icw-avatar" aria-hidden="true">
          <span className="icw-pulse-ring" />
          <span className="icw-pulse-ring icw-pulse-ring--delayed" />
          <span className="icw-avatar-letter">{initial}</span>
        </div>
        <div className="icw-identity-text">
          {/* Known lead → name + number. Unknown number → show the NUMBER as the
              headline (not a generic "Unknown Caller"), and skip the duplicate
              number line. Only fall back to "Unknown Caller" when there's no
              caller ID at all. */}
          <div className="icw-name">{callerName || formatPhoneNumber(callerNumber) || "Unknown Caller"}</div>
          {callerName && (
            <div className="icw-number">{formatPhoneNumber(callerNumber)}</div>
          )}
          {isLiveTransfer ? (
            /* No campaign name here — that's owner-side config, not something
               an agent needs mid-ring. A TV line's tag is the exception: it's
               who the caller thinks they called. */
            <div className="icw-tag">
              {sourceLabel ? `${sourceLabel} · Live Transfer` : "Live Transfer · first to answer takes it"}
            </div>
          ) : isPooledLead ? (
            <div className="icw-tag">Pooled lead · auto-assign on answer</div>
          ) : null}
        </div>
      </div>

      {isCallWaiting ? (
        <div className="icw-controls-waiting">
          <button
            type="button"
            className="icw-btn icw-btn--primary icw-btn--full"
            onClick={() => onAnswer({ holdActive: true })}
          >
            <PauseCircle strokeWidth={2} />
            <span>Hold current &amp; answer</span>
          </button>
          <div className="icw-row-two">
            <button
              type="button"
              className="icw-btn icw-btn--secondary"
              onClick={() => onAnswer({ mergeActive: true })}
            >
              <CornerDownRight strokeWidth={2} />
              <span>Merge 3-way</span>
            </button>
            <button
              type="button"
              className="icw-btn icw-btn--destructive"
              onClick={onDecline}
            >
              <PhoneOff strokeWidth={2} />
              <span>Decline</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="icw-controls">
          <button
            type="button"
            className="icw-btn icw-btn--destructive"
            onClick={onDecline}
          >
            <PhoneOff strokeWidth={2} />
            <span>Decline</span>
          </button>
          <button
            type="button"
            className="icw-btn icw-btn--primary"
            onClick={() => onAnswer()}
          >
            <Phone strokeWidth={2} />
            <span>Answer</span>
          </button>
        </div>
      )}

      {leadId && !isPooledLead && (
        <button type="button" className="icw-lead-link" onClick={handleViewLeadDetails}>
          <span className="icw-lead-link-text">Open lead details</span>
          <ExternalLink strokeWidth={2} />
        </button>
      )}

      <style jsx>{`
        .icw-root {
          position: fixed;
          top: 5rem;
          right: 1.25rem;
          z-index: 50;
          width: 360px;
          padding: 20px 20px 16px;
          color: var(--foreground);
          background: var(--card);
          border: 1px solid var(--border);
          border-radius: var(--radius, 0.625rem);
          overflow: hidden;
          box-shadow:
            0 4px 6px -2px rgb(0 0 0 / 0.05),
            0 20px 40px -12px rgb(0 0 0 / 0.18);
          animation: icw-in 280ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
          font-family: inherit;
        }
        :global(.dark) .icw-root {
          box-shadow:
            0 4px 6px -2px rgb(0 0 0 / 0.3),
            0 20px 40px -12px rgb(0 0 0 / 0.6);
        }
        @keyframes icw-in {
          from {
            opacity: 0;
            transform: translate3d(0, -8px, 0) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translate3d(0, 0, 0) scale(1);
          }
        }

        .icw-accent-bar {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: var(--primary);
          transform-origin: left center;
          animation: icw-bar 540ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
        }
        @keyframes icw-bar {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }

        .icw-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .icw-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 11px;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--muted-foreground);
          font-weight: 600;
        }

        .icw-pulse-dot {
          width: 7px;
          height: 7px;
          border-radius: 999px;
          background: var(--primary);
          animation: icw-dot-pulse 1.6s ease-out infinite;
        }
        @keyframes icw-dot-pulse {
          0%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--primary) 60%, transparent); }
          80%  { box-shadow: 0 0 0 8px color-mix(in srgb, var(--primary) 0%, transparent); }
          100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--primary) 0%, transparent); }
        }

        .icw-top-right {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .icw-duration {
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 12px;
          color: var(--muted-foreground);
          font-variant-numeric: tabular-nums;
        }

        .icw-close {
          width: 26px;
          height: 26px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          border: none;
          background: transparent;
          color: var(--muted-foreground);
          cursor: pointer;
          transition: color 150ms ease, background-color 150ms ease;
        }
        .icw-close :global(svg) {
          width: 14px;
          height: 14px;
        }
        .icw-close:hover {
          color: var(--foreground);
          background: var(--muted);
        }

        .icw-identity {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          margin-bottom: 18px;
        }

        .icw-avatar {
          position: relative;
          flex: 0 0 52px;
          width: 52px;
          height: 52px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          background: color-mix(in srgb, var(--primary) 10%, transparent);
          border: 1px solid color-mix(in srgb, var(--primary) 25%, transparent);
          overflow: visible;
        }

        .icw-avatar-letter {
          font-size: 22px;
          font-weight: 600;
          letter-spacing: -0.01em;
          color: var(--primary);
          line-height: 1;
          position: relative;
          z-index: 1;
        }

        .icw-pulse-ring {
          position: absolute;
          inset: -1px;
          border-radius: 999px;
          border: 2px solid var(--primary);
          opacity: 0;
          animation: icw-ring 2.2s cubic-bezier(0.2, 0.7, 0.3, 1) infinite;
        }
        .icw-pulse-ring--delayed {
          animation-delay: 1.1s;
        }
        @keyframes icw-ring {
          0%   { transform: scale(1); opacity: 0.5; }
          80%  { transform: scale(1.45); opacity: 0; }
          100% { transform: scale(1.45); opacity: 0; }
        }

        .icw-identity-text {
          min-width: 0;
          flex: 1;
        }

        .icw-name {
          font-size: 18px;
          font-weight: 600;
          line-height: 1.2;
          letter-spacing: -0.01em;
          color: var(--foreground);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .icw-number {
          margin-top: 3px;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 13px;
          color: var(--muted-foreground);
          font-variant-numeric: tabular-nums;
        }

        .icw-tag {
          margin-top: 8px;
          display: inline-block;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          color: var(--primary);
          padding: 3px 8px;
          background: color-mix(in srgb, var(--primary) 10%, transparent);
          border: 1px solid color-mix(in srgb, var(--primary) 30%, transparent);
          border-radius: 999px;
        }

        .icw-controls {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
        }
        .icw-controls-waiting {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .icw-row-two {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
        }

        .icw-btn {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 14px;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: -0.005em;
          border-radius: calc(var(--radius, 0.625rem) - 2px);
          border: 1px solid transparent;
          cursor: pointer;
          transition: background-color 160ms ease, color 160ms ease, border-color 160ms ease, transform 100ms ease;
          font-family: inherit;
          white-space: nowrap;
        }
        .icw-btn :global(svg) {
          width: 15px;
          height: 15px;
          flex: 0 0 15px;
        }
        .icw-btn:active {
          transform: translateY(0.5px);
        }
        .icw-btn--full {
          width: 100%;
        }

        .icw-btn--primary {
          background: var(--primary);
          color: var(--primary-foreground);
          border-color: var(--primary);
        }
        .icw-btn--primary:hover {
          background: color-mix(in srgb, var(--primary) 90%, black);
          border-color: color-mix(in srgb, var(--primary) 90%, black);
        }

        .icw-btn--secondary {
          background: color-mix(in srgb, var(--primary) 8%, transparent);
          color: var(--primary);
          border-color: color-mix(in srgb, var(--primary) 25%, transparent);
        }
        .icw-btn--secondary:hover {
          background: color-mix(in srgb, var(--primary) 14%, transparent);
          border-color: color-mix(in srgb, var(--primary) 40%, transparent);
        }

        .icw-btn--destructive {
          background: transparent;
          color: var(--destructive);
          border-color: var(--border);
        }
        .icw-btn--destructive:hover {
          background: color-mix(in srgb, var(--destructive) 8%, transparent);
          border-color: color-mix(in srgb, var(--destructive) 35%, transparent);
        }

        .icw-lead-link {
          margin-top: 14px;
          width: 100%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 9px 12px;
          background: transparent;
          border: 1px dashed color-mix(in srgb, var(--primary) 35%, transparent);
          border-radius: calc(var(--radius, 0.625rem) - 2px);
          color: var(--primary);
          font-size: 12px;
          font-weight: 600;
          letter-spacing: -0.005em;
          cursor: pointer;
          transition: background-color 160ms ease, border-color 160ms ease, transform 100ms ease;
          font-family: inherit;
        }
        .icw-lead-link :global(svg) {
          width: 13px;
          height: 13px;
          transition: transform 160ms ease;
        }
        .icw-lead-link:hover {
          background: color-mix(in srgb, var(--primary) 7%, transparent);
          border-color: color-mix(in srgb, var(--primary) 55%, transparent);
          border-style: solid;
        }
        .icw-lead-link:hover :global(svg) {
          transform: translate(2px, -2px);
        }
        .icw-lead-link:active {
          transform: translateY(0.5px);
        }
        .icw-lead-link-text {
          line-height: 1;
        }
      `}</style>
    </div>
  );
};
